import React, { useCallback, useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import Autocomplete, { createFilterOptions }  from '@mui/material/Autocomplete';
import type { TextFieldProps } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import StyledTextField from '../styled-components/StyledTextField';
import { selectSearch } from '../../../modules/search/selectors';
import { receiveCityRequest } from '../../../modules/search/actions/search';
import { scrollLock } from '../entity-editor/entity-editor-blocks/GeneralFieldsBlock';
import { styles } from './CityAutocomplete.styles';
import { HAS_ILLEGAL_SYMBOLS_PATTERN } from '../../constants/patterns-constants';
import AlertService from '../../services/notification.service';
import { normalizeInput, capitalizedName, makeChangeEvent } from '../../utils';

export type RequestData = {
  inputValue: string;
};

type Option = {
  id?: number;
  city: string;
  isCustom?: boolean;
};

// type TChange = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

const filter = createFilterOptions<Option>();

interface OwnProps {
  label?: string;
  value?: string | null;
  freeSolo?: boolean | undefined;
  name: string;
  disabled?: boolean;
  debounceMs?: number;
  minChars: number;
  onChange?: (e: any) => void;
  onChangeValue?: (value: {}) => void;
  errorFields?: any;
  setErrorFields?: any;
  textFieldProps?: Omit<TextFieldProps, 'label' | 'name'>;
  size?: 'small' | 'medium';
  style?: any | undefined;
  inputValue?: string;
  setInputValue?: any;

}

// type CityAutocompleteProps = OwnProps & ReturnType<typeof mapStateToProps>;
type CityAutocompleteProps = OwnProps;

// const makeChangeEvent = (name: string, value: string): TChange => {
//     return {
//       // @ts-expect-error
//       target: { name, value },
//       // @ts-expect-error
//       currentTarget: { name, value },
//     }
// }

const CityAutocomplete = ({
                            label,
                            value,
                            freeSolo,
                            name,
                            errorFields,
                            disabled,
                            setErrorFields,
                            debounceMs,
                            minChars,
                            onChange,
                            onChangeValue,
                            textFieldProps,
                            inputValue,
                            setInputValue,
                            size,
                            style,
                            ...rest
                          } :CityAutocompleteProps) => {
  const dispatch = useDispatch();
  const { citiesResult, processing } = useSelector(selectSearch);
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextSearch = useRef<boolean>(false);
  const externalUpdatedRef = useRef<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const wasAlertRef = useRef(false);
  const lastTypedRef = useRef<string>("");
  const [anchorWidth, setAnchorWidth] = useState<number>();
  const debouncedFetch = useCallback((q: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const requestData: RequestData = {
      inputValue: q,
    }
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      dispatch(receiveCityRequest(requestData));
    }, debounceMs);

  }, [dispatch, debounceMs]);

  const clearFieldError = useCallback(() => {
    setErrorFields?.((prev: any) => ({...prev, [name]: false}));
  }, [name, setErrorFields]);

  const  uiLoading =  processing || !!debounceTimer.current;

  const selectedOptions = useMemo<Option | null >(() => {
    const val = (value ?? "").trim();
    if (!val) return null;
    return options.find(o => o.city.toLowerCase() === val.toLowerCase()) ?? { city: val };

  }, [value, options])

  const isOptionEqualToValue = useCallback((option: Option, v: Option) =>
      option.city.toLowerCase() === v.city.toLowerCase(),
    []
  );

  const filterCitiesWithCreate = (inputOpts: Option[], params: any) => {
    const filtered = filter(inputOpts, params);
    const { inputValue } = params;
    const hasMatch = inputOpts.some((opt) => opt.city.toLowerCase() === inputValue.toLowerCase());
    if (inputValue && !hasMatch && !!freeSolo) {
      filtered.push({
        city: inputValue,
        isCustom: true,
      })
    }
    return filtered;
  }

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }
  }, []);

  useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element) return;
    const measure = () => setAnchorWidth(element.getBoundingClientRect().width);
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(element);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  }, []);

  useEffect(() => {
    setOptions(Array.isArray(citiesResult) ? citiesResult : [] );
  }, [dispatch, citiesResult]);

  useEffect(() => {
    if (value && value.trim() !== "") {
      externalUpdatedRef.current = true;
      setInputValue(value);
      setOpen(false);
    }
  }, [ value, setInputValue ]);

 useEffect(() => {
   if (suppressNextSearch.current) {
     suppressNextSearch.current = false;
     return;
   }
   if (externalUpdatedRef.current) {
     externalUpdatedRef.current = false;
     return;
   }

   const q = inputValue?.trim();
   if (q  && (q.length < minChars)) {
     setOpen(false);
     setOptions([]);
     if (debounceTimer.current) {
       clearTimeout(debounceTimer.current);
       debounceTimer.current = null;
     }
     return;
   }
   // setOpen(true);
   setOptions([]);
   if  (q) debouncedFetch(q);

 }, [inputValue, minChars, debouncedFetch]);

  useEffect(() => {
    scrollLock(open);
  }, [open]);

  const getLabel = useCallback(
    (opt: Option | string) => {
      return typeof opt === 'string' ? opt : (opt?.city ?? ""); },
    []
  );

  const handleChangeCity =(_: unknown, newValue: Option | null) => {
    const next = newValue?.city ?? "";
    setOpen(false);
    suppressNextSearch.current = true;
    const ev = makeChangeEvent(name, next) as unknown as React.ChangeEvent<HTMLInputElement>;
    onChange?.(ev);
    if (newValue?.city) {
      onChangeValue?.(newValue);
      clearFieldError();
    }
    setInputValue(next);
  }

  const handleInputChangeCity = (_: unknown, newInput: string, reason: string) => {
    if (reason === "clear") {
      setInputValue("")
      setOpen(false);
      setOptions?.([]);
      const ev = makeChangeEvent(name, "") as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange?.(ev);
      onChangeValue?.("");
      wasAlertRef.current = false;
      return;
    }
    if (reason === "reset" || reason === "blur") {
      // setOpen(false);
      return;
    }
    const hasIllegalChars = HAS_ILLEGAL_SYMBOLS_PATTERN.test(newInput);

    const cleaned = freeSolo ? capitalizedName(normalizeInput(newInput)) : normalizeInput(newInput);

    if (hasIllegalChars && !wasAlertRef.current) {
      AlertService.warning(`Invalid characters have been removed from the "${label}" field. Only Latin letters, space, hyphen and apostrophe are allowed`);
      wasAlertRef.current = true;
    }
    if (!hasIllegalChars && cleaned.length === 0) {
      wasAlertRef.current = false;
    }
    if (cleaned !== lastTypedRef.current) {
      lastTypedRef.current = cleaned;
      setInputValue(cleaned);
      setOpen(cleaned.trim().length >= minChars);
      clearFieldError?.();
    }
  }
  const handleFocus = () => {
    if (errorFields) clearFieldError();
  }

  const handleBlur = () => {
    if (!selectedOptions?.city && inputValue?.trim()) {
      setInputValue("")
    }
    if (inputValue?.trim() === "" && textFieldProps?.required) {
      setErrorFields?.((prev: any) => ({...prev, [name]: true}));
      AlertService.warning(`Please fill in the field ${label}.`);
    }
    wasAlertRef.current = false;
  }
  return (
    <>
      <Autocomplete
        {...rest}
        ref={rootRef}
        fullWidth
        freeSolo={!!freeSolo}
        size={size}
        disabled={disabled}
        open={open}
        // onOpen={() => {setOpen(true)}}
        onClose={(_, reason) => {
          if (['toggleInput', 'selectOption', 'escape', 'blur'].includes(reason)) {
            setOpen(false);
          }
        }}
        options={options}
        filterOptions={filterCitiesWithCreate}
        getOptionLabel={getLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={uiLoading}
        loadingText="Loading..."
        noOptionsText={
          (inputValue && (inputValue?.trim().length < minChars)) ? `Please enter at least ${minChars} characters.` : 'No matches'
        }
        value={selectedOptions}
        onChange={handleChangeCity}
        inputValue={inputValue}
        onInputChange={handleInputChangeCity}
        renderOption={(props, option) => {
          if (typeof option === "string" ) {
            return <li {...props}>{option}</li>;
          }
          return  (
            <li {...props}>
              {option.isCustom
              ? `Another city "${option.city}"`
              : option.city }
            </li>
          )
        }}
        autoComplete
        blurOnSelect
        includeInputInList
        // disableCloseOnSelect={false}
        clearOnBlur={false}
        // disablePortal
        sx={{
          width: "100%",
          '& .MuiAutocomplete-clearIndicator': {
            opacity: value ? 1 : 0,
            pointerEvents: value ? 'auto' : 'none',
            transition: 'opacity 120ms',
          },
          "& .MuiInputBase-root": {
            minWidth: 0,
          },
          ...style,
        }}
        slotProps={{
          popper: {
            anchorEl: rootRef.current ?? undefined,
            placement: 'bottom-start',
            style: anchorWidth ? {width: anchorWidth} : undefined,
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [ 0, 0 ]
                }
              },
              {
                name: 'preventOverflow',
                options: {
                  fallbackPlacements: ['top-start']
                }
              },
            ],
          },
          paper: {
            sx: {
              width: 1
            },
          },
        }}
        renderInput={(params) => {
          return (
            <StyledTextField
              {...params}
              label={label}
              name={name}
              error={errorFields}
              onFocus={handleFocus}
              onBlur={handleBlur}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment:
                    (
                       <>
                         {params.InputProps?.endAdornment}
                       </>
                    ),
                  sx: {...styles.inputStyle},
                },
                inputLabel: {
                  ...params.InputLabelProps,
                },
              }}
              {...textFieldProps}
            />
          )
        }
        }
      />
    </>
  );
};

export default CityAutocomplete;